import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import apiClient from '../../../../services/api/apiClient';
import { RapportCarte } from '../../../../utils/types/RapportCarte';
import { IClient } from '../../../../utils/types/IClient';
import ClientSelect from '../../../select/ClientSelect';
import RapportCarteGrid from '../../../grid/RapportCarteGrid';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../others/QueryErrorAlerts';

interface CarteReportProps {
    onClose?: () => void;
    showModal?: boolean;
}

const CarteReportContent: React.FC<CarteReportProps> = ({ showModal }) => {
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: serverDate = null,
        refetch: refetchServerDate,
        isError: isServerDateError,
        error: serverDateError,
    } = useQuery<string>({
        queryKey: ['serverDate'],
        queryFn: async () => {
            const res = await apiClient.get(`/api/data/serverDt`);
            return res.data;
        },
        meta: { title: 'Chargement de la date serveur' },
        retry: 3,
        staleTime: 0,
    });

    const {
        data: reportItems = [],
        isLoading: isReportItemsLoading,
        isError: isReportItemsError,
        error: reportItemsError,
        refetch: refetchReportItems,
    } = useQuery<RapportCarte[]>({
        queryKey: [
            'rapportPrisesCartes',
            {
                codeClient: selectedCardCode,
                dateDebut: startDate,
                dateFin: endDate,
            },
        ],
        queryFn: async () => {
            if (!selectedCardCode || !startDate || !endDate) {
                return [];
            }

            const response = await apiClient.get('/api/cartes/getRapportCarte', {
                params: {
                    codeClient: selectedCardCode,
                    dateDebut: startDate,
                    dateFin: endDate,
                },
            });
            return response.data ?? [];
        },
        meta: { title: 'Chargement du rapport de cartes' },
        enabled: showModal === true && selectedCardCode != null && startDate != null && endDate != null,
        staleTime: 0,
    });

    // ---

    const hanleLoadReportItems = useCallback(() => {
        if (!selectedCardCode || !startDate || !endDate) {
            return;
        }

        refetchReportItems();
    }, [refetchReportItems, selectedCardCode, startDate, endDate]);

    // ---

    useEffect(() => {
        if (clients != null && clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
        }
    }, [clients]);

    useEffect(() => {
        if (serverDate) {
            const serverDt = new Date(serverDate);

            const localYear = serverDt.getFullYear();
            const localMonth = serverDt.getMonth() + 1;
            const localDate = serverDt.getDate();

            const pad = (n: any) => n.toString().padStart(2, '0');

            const startDateString = `${localYear}-${pad(localMonth)}-01`;
            const endDateString = `${localYear}-${pad(localMonth)}-${pad(localDate)}`;

            setStartDate(startDateString);
            setEndDate(endDateString);
        }
    }, [serverDate]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isReportItemsError, error: reportItemsError, title: 'Erreur lors du chargement des prises', refetch: hanleLoadReportItems },
            { isError: isServerDateError, error: serverDateError, title: 'Erreur lors du chargement de la date serveur', refetch: refetchServerDate },
        ],
        [isClientsError, clientsError, refetchClients, isReportItemsError, reportItemsError, hanleLoadReportItems, isServerDateError, serverDateError, refetchServerDate],
    );

    // ---

    return (
        <>
            <div>
                <div>
                    <div className="row align-items-end">
                        <div className="col-sm-5 form-group">
                            <label className="form-label">Client *</label>
                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                        </div>

                        <div className="col-md-2 col-sm-4 form-group">
                            <label className="form-label">Date début *</label>
                            <input
                                type="date"
                                className="form-control"
                                value={startDate ?? ''}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                }}
                            />
                        </div>

                        <div className="col-md-2 col-ms-4 form-group">
                            <label className="form-label">Date fin *</label>
                            <input
                                type="date"
                                className="form-control"
                                value={endDate ?? ''}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className="row align-items-end mt-2">
                        <div className="col-sm-auto form-group">
                            <button type="submit" className="btn btn-default" disabled={isReportItemsLoading}>
                                {isReportItemsLoading ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        &nbsp; Générer
                                    </>
                                ) : (
                                    'Générer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-2">
                <QueryErrorAlerts queries={queryErrors} />
                <RapportCarteGrid reportItems={reportItems} />
            </div>
        </>
    );
};

const CarteReport: React.FC<CarteReportProps> = ({ onClose }) => {
    const [showModal, setShowModal] = useState(false);

    const handleClose = () => {
        setShowModal(false);
        onClose?.();
    };
    const handleShow = () => setShowModal(true);

    // ---

    return (
        <>
            <button onClick={handleShow} className="btn btn-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-file-analytics">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                    <path d="M9 17l0 -5" />
                    <path d="M12 17l0 -1" />
                    <path d="M15 17l0 -3" />
                </svg>
                Rapport
            </button>

            <Modal show={showModal} onHide={handleClose} size="xl" scrollable backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Rapport Carte</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <CarteReportContent showModal={showModal} />
                </Modal.Body>
                
            </Modal>
        </>
    );
};

export default CarteReport;
